export const MIME_PDF = ["application/pdf"]
export const MIME_IMAGE = ["image/jpeg", "image/png", "image/webp", "image/jpg", "image/gif"]
export const MIME_DOCUMENT = [
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
  "text/html",
]
export const MIME_CSV = ["text/csv", "application/csv"]
export const MIME_SPREADSHEET = [
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
]
export const MIME_PRESENTATION = [
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]

export const MIME_GPT_DEFAULT = [
  ...MIME_IMAGE,
  ...MIME_PDF,
  ...MIME_CSV,
  ...MIME_DOCUMENT,
  ...MIME_SPREADSHEET,
  ...MIME_PRESENTATION,
]

export const GPT_EXTENSIONS = [
  "jpg",
  "jpeg",
  "png",
  "webp",
  "gif",
  "pdf",
  "csv",
  "doc",
  "docx",
  "txt",
  "html",
  "htm",
  "xls",
  "xlsx",
  "pptx",
] as const

export const GPT_MAX_FILES = 20
export const GPT_MAX_FILE_BYTES = 100 * 1024 * 1024

const GPT_MIME_TYPES = new Set<string>(MIME_GPT_DEFAULT)
const GPT_EXTENSION_SET = new Set<string>(GPT_EXTENSIONS)

export const GPT_FILE_ACCEPT = [...GPT_EXTENSIONS, ...MIME_GPT_DEFAULT]
  .map((token) => (token.includes("/") ? token : `.${token}`))
  .join(",")

export type GptFileRejection = "type" | "size" | "count" | "total"

type NamedFile = { name: string; type: string; size: number }

export function isSupportedGptFile(file: { name: string; type: string }): boolean {
  const type = file.type.toLowerCase()
  if (type && GPT_MIME_TYPES.has(type)) return true
  const extension = file.name.split(".").pop()?.toLowerCase() ?? ""
  return GPT_EXTENSION_SET.has(extension)
}

export function rejectionForGptFile(
  file: NamedFile,
  acceptedCount: number,
  acceptedBytes: number,
): GptFileRejection | null {
  if (acceptedCount >= GPT_MAX_FILES) return "count"
  if (!isSupportedGptFile(file)) return "type"
  if (file.size > GPT_MAX_FILE_BYTES) return "size"
  if (acceptedBytes + file.size > GPT_MAX_FILE_BYTES) return "total"
  return null
}

export function fileMediaType(file: { name: string; type: string }): string {
  if (file.type && file.type !== "application/octet-stream") return file.type
  const extension = file.name.split(".").pop()?.toLowerCase()
  switch (extension) {
    case "jpg":
    case "jpeg":
      return "image/jpeg"
    case "png":
      return "image/png"
    case "webp":
      return "image/webp"
    case "gif":
      return "image/gif"
    case "pdf":
      return "application/pdf"
    case "csv":
      return "text/csv"
    case "txt":
      return "text/plain"
    case "html":
    case "htm":
      return "text/html"
    case "doc":
      return "application/msword"
    case "docx":
      return "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
    case "xls":
      return "application/vnd.ms-excel"
    case "xlsx":
      return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    case "pptx":
      return "application/vnd.openxmlformats-officedocument.presentationml.presentation"
    default:
      return "application/octet-stream"
  }
}
